import { Component, ElementRef, Input, ViewChild, forwardRef } from '@angular/core';
import { BaseFormComponent } from '../../base-form.component';
import { FormService } from '../../../../services/form/form.service';
import * as ld from 'lodash';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  moduleId: module.id,
  selector: 'select-basic',
  host: {
    '(document:click)': 'handleClick($event)',
  },
  templateUrl: './select-basic.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectBasicComponent),
      multi: true,
    },
  ],
})
export class SelectBasicComponent extends BaseFormComponent implements ControlValueAccessor {
  @Input() propertyName: string;
  @Input() placeholder: string;
  @Input() optionValues?: Array<object>; // let components pass in values if they want
  @Input() noLabel?: boolean;
  @Input() label?: string;
  @Input() formGroupComponent?: boolean;
  @Input() isDisabled?: boolean;
  @Input() showSelectedItem = true;
  @ViewChild('dropdownUl') dropdown: ElementRef;
  @ViewChild('selectInput') inputBox: ElementRef;

  public selectedIndex: number = 0;

  options: Array<any>;
  selectedValue: string;
  showDropdown: boolean = false;
  elementRef: ElementRef;
  isError: boolean;
  invalidOptions: Array<string> = [];

  onChange;
  onTouched;
  selectedItem: string;

  constructor(formService: FormService, elementRef: ElementRef) {
    super();
    this.elementRef = elementRef;
    this.formService = formService;
    this.isError = false;
  }

  protected incrementSelectedIndex() {
    this.selectedIndex = this.selectedIndex < this.options.length - 1 ? this.selectedIndex + 1 : this.selectedIndex;
  }

  protected decrementSelectedIndex() {
    this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : this.selectedIndex;
  }

  keyUpPress(event: KeyboardEvent): void {
    if (event.code === 'Tab') {
      this.showBox();
    }
  }

  keyDownPress(event: KeyboardEvent): boolean {
    if (event.code === 'Tab') {
      this.hideBox();
      return true;
    }
    this.handleKeyInteractions(event);
    return false;
  }

  handleClick(event: MouseEvent) {
    const clickedComponent = event.target;
    let inside = false;

    if (this.isDisabled) {
      return;
    }

    if (clickedComponent === this.inputBox.nativeElement) {
      this.toggleBox();
      return false;
    }

    if (!this.dropdown) {
      return true;
    }

    if (clickedComponent === this.dropdown.nativeElement) {
      inside = true;
    }

    const parentNode = (<HTMLElement>clickedComponent).parentNode;
    if (parentNode && parentNode === this.dropdown.nativeElement) {
      inside = true;
    }

    if (!inside) {
      this.hideBox();
      return false;
    }
    return false;
  }

  /*
        first touch router of key events.
     */
  async handleKeyInteractions(event: KeyboardEvent) {
    const code = event.code;
    this.keyUpPress(event);
    if (code === 'Enter') {
      // enter
      await this.trigger(this.options[this.selectedIndex]);
    } else if (code === 'Space') {
      if (this.showDropdown) {
        this.trigger(this.options[this.selectedIndex]);
      } else {
        this.showDropdown = true;
      }
    } else if (code === 'Escape') {
      this.hideBox();
    } else if (code === 'ArrowUp') {
      const options = { block: 'nearest', inline: 'start' };
      const index = this.selectedIndex - 1 < 0 ? 0 : this.selectedIndex - 1;
      const option = this.dropdown.nativeElement.children[index];
      option.scrollIntoView(options);
      this.decrementSelectedIndex();
    } else if (code === 'ArrowDown') {
      this.incrementSelectedIndex();
      const options = { block: 'nearest', inline: 'end' };
      const option = this.dropdown.nativeElement.children[this.selectedIndex];
      option.scrollIntoView(options);
    }
    return false;
  }

  private resetSelectedIndex() {
    this.selectedIndex = 0;
  }

  ngOnInit() {
    super.ngOnInit();
    this.prop = this.propertyName;
    this.componentId = this.propertyName;
    this.updateFromModel(); // For the initial value
  }

  setInvalidOptions(options: Array<string>): void {
    this.invalidOptions = options;
  }

  setOptions() {
    this.options = [];

    if (this.optionValues !== undefined && this.optionValues.length > 0) {
      this.options = this.optionValues;
    } else {
      const values = this.formService.getValuesFor(this.prop);
      const descriptionsObj = this.formService.getDescriptionsFor(this.prop);

      //use descriptions incase if encountering descriptions as well
      if (descriptionsObj) {
        ld.map(values, (key) => {
          const description =
            typeof descriptionsObj[key.toString()] === 'object'
              ? descriptionsObj[key.toString()].text
              : descriptionsObj[key.toString()];
          if (description) {
            this.options.push({
              value: key,
              description: description,
            });
          }
        });
      } else {
        if (values instanceof Array) {
          values.forEach((value) => {
            this.options.push({
              value: value,
              description: value,
            });
          });
        } else {
          ld.map(values, (description, key) => {
            this.options.push({
              value: key,
              description: description,
            });
          });
        }
      }
    }
  }

  updateFromModel() {
    this.setOptions();

    let value = this.value;
    if (!this.formGroupComponent) {
      value = this.getValue(this.prop);
    }

    let valueStr = '';
    if (value !== null && typeof value !== 'undefined') {
      valueStr = value + '';
    }
    if (valueStr !== this.selectedItem) {
      const option = this.options.find((opt) => opt.value === valueStr);
      this.selectedItem = option && option.description ? option.description : valueStr;
    }
  }

  resetValue() {
    this.selectedItem = '';
    this.selectedValue = '';
  }

  async triggerBase(option): Promise<boolean> {
    this.selectedItem = option.description;
    this.selectedValue = option.value;
    this.resetSelectedIndex();
    this.hideBox();
    this.isError = false;

    if (!this.formGroupComponent) {
      this.isError = !(await this.validateAndSet(this.prop, option.value));
    }

    return this.isError;
  }

  async trigger(option): Promise<boolean> {
    if (this.invalidOptions.indexOf(option.value) >= 0) {
      return;
    }
    const isError = await this.triggerBase(option);
    if (this.onChange && this.onTouched) {
      this.value = this.selectedValue;
    }
    this.valueChanged.emit();
    return isError;
  }

  set value(value: string) {
    this.onChange(value);
    this.onTouched();
  }

  get value(): string {
    return this.selectedValue;
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  writeValue(value): void {
    if (value) {
      this.selectedValue = value;
      if (this.selectedItem !== undefined) {
        const opt = this.options.find((opt) => opt.value === value);
        this.selectedItem = opt.description;
      }
    }
  }

  getErrorMessage(): string {
    return this.errorMessage[this.prop];
  }

  getClasses() {
    const isComplete = !this.isError && !!this.selectedItem && this.selectedItem !== 'null';
    return {
      complete: isComplete,
      error: this.isError,
    };
  }

  showBox(): void {
    this.showDropdown = true;
  }

  toggleBox(): void {
    this.showDropdown = !this.showDropdown;
  }

  hideBox(): void {
    this.showDropdown = false;
  }

  onblur(): void {
    if (this.onBlur && this.onBlur.observers.length !== 0) {
      this.onBlur.emit();
    }
  }
}
